import { LoadingGuard, icons } from '@lifebit-ai/lds'
import React from 'react'
import { useParams } from 'react-router-dom'

import useQueryParams from 'hooks/useQueryParams'
import { AirlockModuleIcon } from 'img'
import AuthWrapper from 'modules/login/AuthWrapper'
import { formatDateAtTime } from 'utils/date'

import * as styles from './DownloadPage.styles'
import { useSignedUrlForApprovedResult } from './hooks'

const { Loading } = LoadingGuard

export const DOWNLOAD_PAGE_ERROR_VIEW_TEST_ID = 'download-page-error-view-test-id'
export const DOWNLOAD_PAGE_DOWNLOAD_ERROR_VIEW_TEST_ID = 'download-page-download-error-view-test-id'
export const DOWNLOAD_PAGE_SUCCESS_VIEW_TEST_ID = 'download-page-success-view-test-id'

const DownloadPage: React.FC = () => {
  const { resultId } = useParams<{ resultId: string }>()
  const queryParams = useQueryParams()
  const token = queryParams.get('token')

  const { expiresAt, isLinkExpired, isDownloadError, isLoading } = useSignedUrlForApprovedResult({ resultId, token })

  if (isLinkExpired) {
    const description = expiresAt
      ? `Download link expired on ${formatDateAtTime(expiresAt)}. Please re-submit your download request.`
      : 'Download links have expired. Please re-submit your download request.'

    return (
      <AuthWrapper>
        <styles.LogoContainer data-testid={DOWNLOAD_PAGE_ERROR_VIEW_TEST_ID}>
          <AirlockModuleIcon />
          <styles.LogoOverlay>
            <icons.Clock />
          </styles.LogoOverlay>
        </styles.LogoContainer>
        <styles.Title>Download link expired</styles.Title>
        <styles.Description>{description}</styles.Description>
      </AuthWrapper>
    )
  }

  if (isDownloadError) {
    return (
      <AuthWrapper>
        <styles.LogoContainer data-testid={DOWNLOAD_PAGE_DOWNLOAD_ERROR_VIEW_TEST_ID}>
          <AirlockModuleIcon />
          <styles.ErrorLogoOverlay>
            <icons.CrossInCircle />
          </styles.ErrorLogoOverlay>
        </styles.LogoContainer>
        <styles.Title>Download failed</styles.Title>
        <styles.Description>Something went wrong while downloading your file. Please try again.</styles.Description>
      </AuthWrapper>
    )
  }

  if (isLoading) {
    return (
      <AuthWrapper>
        <Loading text="Preparing file to download..." />
      </AuthWrapper>
    )
  }

  return (
    <AuthWrapper>
      <styles.LogoContainer data-testid={DOWNLOAD_PAGE_SUCCESS_VIEW_TEST_ID}>
        <AirlockModuleIcon />
        <styles.LogoOverlay success>
          <icons.CheckInCircleSmall />
        </styles.LogoOverlay>
      </styles.LogoContainer>
      <styles.Title>Download started</styles.Title>
      <styles.Description>If it did not start, please try again by refreshing this page.</styles.Description>
    </AuthWrapper>
  )
}

export default DownloadPage
